'use client'

import { z } from 'zod'

import {
  billingAttemptSchema,
  billingMethodsSchema,
  billingSetupSchema,
  brandpaySessionSchema,
} from '@/features/veluvit/schemas/billing.schema'
import { ApiError, api } from '@/features/veluvit/services/api'
import { checkedTossCallback } from '@/features/veluvit/services/payment.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type {
  BillingAttempt,
  BillingSetup,
  PendingBillingSetup,
} from '@/features/veluvit/schemas/billing.schema'
import type { PaymentModel } from '@/features/veluvit/schemas/payment.schema'

function scope(
  ownerId: string,
  identity = useSessionStore.getState().identity
) {
  const state = useSessionStore.getState()
  if (
    state.identity !== identity ||
    state.data?.user?.id !== ownerId ||
    state.data.user.guest
  )
    throw new ApiError(
      '계정이 바뀌어 결제수단 작업을 종료했어요.',
      0,
      'PAYMENT_OWNER_CHANGED'
    )
  return identity
}

export async function readBillingMethods(
  ownerId: string,
  signal?: AbortSignal
) {
  const identity = scope(ownerId)
  const result = billingMethodsSchema.parse(
    await api<unknown>('/payment/billing/', undefined, 'GET', signal)
  )
  scope(ownerId, identity)
  return result
}

export function readBillingAttempt(ownerId: string): BillingAttempt | null {
  scope(ownerId)
  const raw = sessionStorage.getItem('veluvit:billing:setup:' + ownerId)
  if (!raw) return null
  const result = billingAttemptSchema.parse(JSON.parse(raw))
  if (result.ownerId !== ownerId)
    throw new Error('등록 요청의 계정이 일치하지 않아요.')
  return result
}

export function clearBillingAttempt(ownerId: string, setupId: string) {
  if (readBillingAttempt(ownerId)?.setupId === setupId)
    sessionStorage.removeItem('veluvit:billing:setup:' + ownerId)
}

export async function createBillingSetup(
  ownerId: string,
  optionId: string,
  consentVersion: string,
  model: PaymentModel
) {
  const identity = scope(ownerId)
  const existing = readBillingAttempt(ownerId)
  const attempt: BillingAttempt =
    existing?.optionId === optionId &&
    existing.consentVersion === consentVersion &&
    existing.model === model
      ? existing
      : {
          version: 1,
          ownerId,
          optionId,
          consentVersion,
          model,
          requestId: crypto.randomUUID(),
        }
  sessionStorage.setItem(
    'veluvit:billing:setup:' + ownerId,
    JSON.stringify(attempt)
  )
  const result = billingSetupSchema.parse(
    await api<unknown>(
      '/payment/billing/setup/',
      {
        optionId,
        consentVersion,
        model,
        requestId: attempt.requestId,
        returnOrigin: window.location.origin,
      },
      'POST'
    )
  )
  scope(ownerId, identity)
  if (result.option.id !== optionId)
    throw new Error('선택한 결제수단과 등록 정보가 달라요.')
  if (readBillingAttempt(ownerId)?.requestId === attempt.requestId)
    sessionStorage.setItem(
      'veluvit:billing:setup:' + ownerId,
      JSON.stringify({ ...attempt, setupId: result.id })
    )
  return result
}

export async function readBillingSetup(
  ownerId: string,
  id: string,
  signal?: AbortSignal
): Promise<BillingSetup> {
  const identity = scope(ownerId)
  const result = billingSetupSchema.parse(
    await api<unknown>(
      `/payment/billing/setup/${encodeURIComponent(id)}/`,
      undefined,
      'GET',
      signal
    )
  )
  scope(ownerId, identity)
  if (result.id !== id) throw new Error('다른 결제수단 등록 정보가 도착했어요.')
  return result
}

export function validateBillingSetup(
  setup: PendingBillingSetup,
  model: PaymentModel
) {
  if (
    setup.clientKey.startsWith('test_') !== setup.sandbox ||
    !setup.option.available ||
    !setup.option.models.includes(model)
  )
    throw new Error('현재 환경에서 등록할 수 없는 결제수단이에요.')
  checkedTossCallback(setup.successUrl)
  checkedTossCallback(setup.failUrl)
  if (setup.brandpayRedirectUrl) checkedTossCallback(setup.brandpayRedirectUrl)
  if (setup.option.method === 'BRANDPAY' && !setup.brandpayRedirectUrl)
    throw new Error('간편결제 인증 주소를 확인하지 못했어요.')
  return setup
}

export async function confirmBrandpayBilling(ownerId: string, id: string) {
  const identity = scope(ownerId)
  const result = billingSetupSchema.parse(
    await api<unknown>(
      `/payment/billing/setup/${encodeURIComponent(id)}/confirm/`,
      {},
      'POST'
    )
  )
  scope(ownerId, identity)
  if (result.id !== id) throw new Error('다른 결제수단 등록 결과가 도착했어요.')
  return result
}

export async function readBrandpaySession(
  ownerId: string,
  signal?: AbortSignal
) {
  const identity = scope(ownerId)
  const result = brandpaySessionSchema.parse(
    await api<unknown>('/payment/brandpay/session/', undefined, 'GET', signal)
  )
  scope(ownerId, identity)
  checkedTossCallback(result.redirectUrl)
  return result
}

export async function removeBillingMethod(
  ownerId: string,
  id: string,
  signal?: AbortSignal
) {
  const identity = scope(ownerId)
  await api<unknown>(
    `/payment/brandpay/method/${encodeURIComponent(z.string().min(1).parse(id))}/`,
    undefined,
    'DELETE',
    signal
  )
  scope(ownerId, identity)
}
